import { Children, isValidElement, type ReactNode } from 'react';

import cn from 'classnames';

import { Highlight } from 'ui/atoms/Highlight/Highlight';

import styles from './TitleBlock.module.scss';

import type { TitleBlockProps } from './TitleBlock.types';

const ACCENT_CLASS: Record<Exclude<NonNullable<TitleBlockProps['accent']>, 'none'>, string> = {
  blue: 'accentBlue',
  indigo: 'accentIndigo',
  aurora: 'accentAurora',
};

const ACCENT_MARKUP_TYPES = new Set<unknown>(['span', 'em', Highlight]);

const hasAccentMarkup = (node: ReactNode): boolean =>
  Children.toArray(node).some((child) => {
    if (!isValidElement(child)) {
      return false;
    }

    if (ACCENT_MARKUP_TYPES.has(child.type)) {
      return true;
    }

    return hasAccentMarkup((child.props as { children?: ReactNode }).children);
  });

export const TitleBlock = ({
  extra,
  title,
  description,
  size = 'section',
  tag,
  theme = 'light',
  align = 'center',
  layout = 'stacked',
  accent,
  fullWidth = false,
  className,
  descriptionClassName,
}: TitleBlockProps) => {
  const Tag = tag ?? (size === 'page' ? 'h1' : 'h2');

  const resolvedAccent = accent ?? (size === 'page' ? 'blue' : 'indigo');
  const accentClass = resolvedAccent !== 'none' ? styles[ACCENT_CLASS[resolvedAccent]] : undefined;
  const titleContent = accentClass && title != null && !hasAccentMarkup(title) ? <span>{title}</span> : title;

  return (
    <div
      className={cn(
        styles.wrapper,
        theme === 'dark' && styles.dark,
        align === 'left' && styles.left,
        layout === 'split' && styles.split,
        fullWidth && styles.fullWidth,
        className,
      )}
    >
      {extra && <span className={styles.extra}>{extra}</span>}

      {title && (
        <Tag className={cn(styles.title, size === 'page' ? styles.titlePage : styles.titleSection, accentClass)}>
          {titleContent}
        </Tag>
      )}

      {description && (
        <p
          className={cn(
            styles.description,
            size === 'page' ? styles.descriptionPage : styles.descriptionSection,
            descriptionClassName,
          )}
        >
          {description}
        </p>
      )}
    </div>
  );
};
